import React, {
    forwardRef,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { Box, Portal } from '@mui/material';

export type ZoomImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
    zoomOnClick?: boolean;
    zoomWidth?: number | string;
    zoomHeight?: number | string;
    overlayBg?: string;
    transitionDuration?: number;
};

const DEFAULT_ZOOM_WIDTH = '75vw';
const DEFAULT_ZOOM_HEIGHT = '85vh';
const DEFAULT_OVERLAY_BG = 'rgba(0, 0, 0, 0.72)';
const DEFAULT_DURATION = 280;

type Rect = {
    top: number;
    left: number;
    width: number;
    height: number;
};

const parseSize = (
    value: number | string | undefined,
    viewportSize: number,
    fallbackRatio: number,
) => {
    if (typeof value === 'number') {
        return value;
    }

    if (typeof value === 'string') {
        if (value.endsWith('vw')) {
            return (
                parseFloat(value) / 100
            ) * window.innerWidth;
        }

        if (value.endsWith('vh')) {
            return (
                parseFloat(value) / 100
            ) * window.innerHeight;
        }

        if (value.endsWith('px')) {
            return parseFloat(value);
        }

        const numeric = parseFloat(value);
        if (!Number.isNaN(numeric)) {
            return numeric;
        }
    }

    return viewportSize * fallbackRatio;
};

const getTargetSize = (
    naturalWidth: number,
    naturalHeight: number,
    maxWidth: number,
    maxHeight: number,
) => {
    if (!naturalWidth || !naturalHeight) {
        return {
            width: maxWidth,
            height: maxHeight,
        };
    }

    const ratio = naturalWidth / naturalHeight;

    let width = maxWidth;
    let height = width / ratio;

    if (height > maxHeight) {
        height = maxHeight;
        width = height * ratio;
    }

    return { width, height };
};

export const ZoomImage = forwardRef<HTMLImageElement, ZoomImageProps>(
    (
        {
            zoomOnClick = false,
            zoomWidth = DEFAULT_ZOOM_WIDTH,
            zoomHeight = DEFAULT_ZOOM_HEIGHT,
            overlayBg = DEFAULT_OVERLAY_BG,
            transitionDuration = DEFAULT_DURATION,
            style,
            onClick,
            onLoad,
            ...imgProps
        },
        forwardedRef,
    ) => {
        const imgRef = useRef<HTMLImageElement | null>(null);

        const [ isOpen, setIsOpen ] = useState(false);
        const [ isAnimatingIn, setIsAnimatingIn ] = useState(false);
        const [ isAnimatingOut, setIsAnimatingOut ] = useState(false);
        const [ originRect, setOriginRect ] = useState<Rect | null>(null);
        const [ targetRect, setTargetRect ] = useState<Rect | null>(null);
        const [ naturalSize, setNaturalSize ] = useState({ width: 0, height: 0 });

        const mergedRef = useCallback(
            (node: HTMLImageElement | null) => {
                imgRef.current = node;

                if (typeof forwardedRef === 'function') {
                    forwardedRef(node);
                } else if (forwardedRef) {
                    forwardedRef.current = node;
                }
            },
            [ forwardedRef ],
        );

        const calculateRects = useCallback(() => {
            const img = imgRef.current;
            if (!img) {
                return null;
            }

            const rect = img.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            const maxWidth = parseSize(zoomWidth, viewportWidth, 0.75);
            const maxHeight = parseSize(zoomHeight, viewportHeight, 0.85);

            const { width, height } = getTargetSize(
                naturalSize.width || img.naturalWidth || rect.width,
                naturalSize.height || img.naturalHeight || rect.height,
                maxWidth,
                maxHeight,
            );

            return {
                origin: {
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height,
                },
                target: {
                    top: (
                        viewportHeight - height
                    ) / 2,
                    left: (
                        viewportWidth - width
                    ) / 2,
                    width,
                    height,
                },
            };
        }, [ naturalSize.height, naturalSize.width, zoomHeight, zoomWidth ]);

        const openZoom = useCallback(() => {
            if (!zoomOnClick) {
                return;
            }

            const rects = calculateRects();
            if (!rects) {
                return;
            }

            setOriginRect(rects.origin);
            setTargetRect(rects.target);
            setIsOpen(true);
            setIsAnimatingOut(false);

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setIsAnimatingIn(true);
                });
            });
        }, [ calculateRects, zoomOnClick ]);

        const closeZoom = useCallback(() => {
            setIsAnimatingIn(false);
            setIsAnimatingOut(true);

            window.setTimeout(() => {
                setIsOpen(false);
                setIsAnimatingOut(false);
            }, transitionDuration);
        }, [ transitionDuration ]);

        const handleClick = (e: React.MouseEvent<HTMLImageElement>) => {
            onClick?.(e);

            if (!zoomOnClick) {
                return;
            }

            openZoom();
        };

        const handleOverlayClick = () => {
            closeZoom();
        };

        const handleOverlayImageClick = (
            e: React.MouseEvent<HTMLImageElement>,
        ) => {
            e.stopPropagation();
        };

        const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
            const img = e.currentTarget;

            setNaturalSize({
                width: img.naturalWidth,
                height: img.naturalHeight,
            });

            onLoad?.(e);
        };

        useEffect(() => {
            if (!isOpen) {
                return;
            }

            const handleResize = () => {
                const rects = calculateRects();
                if (!rects) {
                    return;
                }

                setOriginRect(rects.origin);
                setTargetRect(rects.target);
            };

            const handleKeyDown = (e: KeyboardEvent) => {
                if (e.key === 'Escape') {
                    closeZoom();
                }
            };

            window.addEventListener('resize', handleResize);
            window.addEventListener('scroll', handleResize, true);
            window.addEventListener('keydown', handleKeyDown);

            return () => {
                window.removeEventListener('resize', handleResize);
                window.removeEventListener('scroll', handleResize, true);
                window.removeEventListener('keydown', handleKeyDown);
            };
        }, [ calculateRects, closeZoom, isOpen ]);

        const currentRect =
            isAnimatingIn && targetRect
                ? targetRect
                : isAnimatingOut && originRect
                    ? originRect
                    : originRect;

        return (
            <>
                <Box
                    component="img"
                    ref={mergedRef}
                    {...imgProps}
                    onClick={handleClick}
                    onLoad={handleImageLoad}
                    sx={{
                        display: 'block',
                        maxWidth: '100%',
                        height: 'auto',
                        cursor: zoomOnClick ? 'zoom-in' : style?.cursor,
                        ...style,
                    }}
                />

                {zoomOnClick && isOpen && originRect && targetRect && currentRect && (
                    <Portal>
                        <Box
                            onClick={handleOverlayClick}
                            sx={{
                                position: 'fixed',
                                inset: 0,
                                zIndex: 9999,
                                backgroundColor:
                                    isAnimatingIn && !isAnimatingOut
                                        ? overlayBg
                                        : 'rgba(0, 0, 0, 0)',
                                transition: `background-color ${transitionDuration}ms ease`,
                            }}
                        >
                            <Box
                                component="img"
                                {...imgProps}
                                alt={imgProps.alt}
                                src={imgProps.src}
                                draggable={false}
                                onClick={handleOverlayImageClick}
                                sx={{
                                    position: 'fixed',
                                    top: currentRect.top,
                                    left: currentRect.left,
                                    width: currentRect.width,
                                    height: currentRect.height,
                                    objectFit: 'contain',
                                    borderRadius: 1,
                                    transition: `all ${transitionDuration}ms ease`,
                                    cursor: 'default',
                                    userSelect: 'none',
                                    boxShadow:
                                        isAnimatingIn && !isAnimatingOut
                                            ? '0 20px 80px rgba(0, 0, 0, 0.35)'
                                            : '0 8px 20px rgba(0, 0, 0, 0.15)',
                                }}
                            />
                        </Box>
                    </Portal>
                )}
            </>
        );
    },
);

export default ZoomImage;
